/**
 * `useLancers` — the roster's lancers, resolved for CODEX's LANCERS section (`decisions/0205`).
 *
 * The filter and the join are pure (`routes/home/rolodex/lancers.ts`); this hook adds the three
 * lookups a render needs: the player slots (which pilots count), the owner's slot name, and the
 * compendium names of each pilot's frame, talents and skill triggers. Every pilot field it reads
 * is `"shown"` in `SHARED_SHEET_DISCLOSURE` (`decisions/0140`).
 */
import { useMemo } from "react";
import { resolveActiveMechId } from "@lancer-vtt/domain";
import { lancerPilots, type Lancer } from "../../routes/home/rolodex/lancers";
import { useCompendiumContext } from "../CompendiumProvider";
import { useGameSessionContext } from "../game/GameSessionProvider";
import { useSlotNames } from "../game/useSlotNames";
import type { UseRoster } from "../roster/useRemoteRoster";

export function useLancers(roster: UseRoster): Lancer[] {
  const { slots, actorId, role } = useGameSessionContext();
  const slotName = useSlotNames();
  const { compendium } = useCompendiumContext();
  const pilots = roster.state?.pilots;
  return useMemo(() => {
    if (pilots === undefined) return [];
    const playerSlots = new Set(slots.filter((s) => s.role === "player").map((s) => s.id));
    const named = (type: "talent" | "skill", ids: Record<string, number>) =>
      Object.entries(ids)
        .filter(([, rank]) => rank > 0)
        .map(([id]) => compendium.getById(type, id)?.name ?? id);
    return lancerPilots(pilots, playerSlots).map((pilot) => {
      const active = pilot.mechs.find((m) => m.id === resolveActiveMechId(pilot));
      const frame = active?.frameId ? (compendium.getById("frame", active.frameId)?.name ?? active.frameId) : "";
      const background = pilot.background?.trim();
      return {
        pilotId: pilot.id,
        callsign: pilot.callsign,
        name: pilot.name,
        player: slotName(pilot.owner!),
        ...(background ? { background } : {}),
        ...(pilot.image ? { image: pilot.image } : {}),
        level: pilot.level,
        mech: active ? { name: active.name, frame } : null,
        talents: named("talent", pilot.talents),
        triggers: named("skill", pilot.skills),
        own: role === "player" && pilot.owner === actorId,
      };
    });
  }, [pilots, slots, slotName, compendium, actorId, role]);
}
